import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Trans } from '@lingui/macro'
import { IconRetry, IconTick } from '@yoti/ui-kit-components-react/icons';
import SharingStatuses from '@yoti-web-share/share-flow/src/SharingStatuses'
import * as CLOSE_TYPES from '../../constants/closeTypes'
import withClientAndShare from '../../hoc/withClientAndShare'
import ShareButton from '../../components/ShareButton'
import YotiButtonLink from '../../components/YotiButtonLink'
import PartnershipApps, { PARTNERSHIP_APPS_LAYOUTS } from '../../components/PartnershipApps'

import './ShareToApp.scss'

export const ShareToApp = ({
  clientError,
  clientConfiguration,
  clientClosingType,
  shareActions,
  shareResuming,
  shareActive,
  shareStatus,
  shareQrCodeAppLinkUrl,
  shareError
}) => {
  const { learnMoreLink } = clientConfiguration
  const { initialise, standByUntilUserReturn, openAppLink } = shareActions

  let icon
  let content = <Trans>Loading</Trans>
  let buttonEnabled = false

  const hasErrors = clientError || shareError

  const hasCompleted = shareStatus === SharingStatuses.COMPLETED
  const hasFailed = [SharingStatuses.FAILED, SharingStatuses.REJECTED, SharingStatuses.TIMED_OUT].includes(shareStatus)
  const displayAsResuming = shareResuming || shareStatus === SharingStatuses.RESUMED

  const useDirectAppLink = useMemo(() => !!(clientConfiguration && clientConfiguration.useResumeMobileFlow), [clientConfiguration])

  if (hasErrors) {
    content = <Trans>Not available</Trans>
  } else if (hasFailed) {
    content = <Trans>Try again</Trans>
    icon = <IconRetry />
    buttonEnabled = true
  } else if (hasCompleted) {
    content = clientClosingType === CLOSE_TYPES.REDIRECT
      ? <Trans>Redirecting</Trans>
      : <Trans>Completed</Trans>
    icon = <IconTick />
  } else if (displayAsResuming) {
    content = <Trans>Resuming...</Trans>
  } else if (shareActive && shareQrCodeAppLinkUrl) {
    content = <Trans>Use Yoti</Trans>
    buttonEnabled = true
  }

  const onOpenLink = useCallback(() => {
    openAppLink(shareQrCodeAppLinkUrl, useDirectAppLink)
    if (useDirectAppLink) standByUntilUserReturn()
  }, [
    shareQrCodeAppLinkUrl,
    useDirectAppLink,
    standByUntilUserReturn,
    openAppLink
  ])

  const onRetry = useCallback(() => {
    initialise()
  }, [initialise])

  return (
    <div className="share-to-app">
      <ShareButton
        onClick={hasFailed ? onRetry : onOpenLink}
        disabled={!buttonEnabled}
        icon={icon}
      >
        {content}
      </ShareButton>
      {learnMoreLink && <YotiButtonLink learnMoreLink={learnMoreLink} />}
      <PartnershipApps layout={PARTNERSHIP_APPS_LAYOUTS.INLINE} />
    </div>
  )
}

ShareToApp.defaultProps = {
  clientError: undefined,
  shareError: undefined,
  clientClosingType: '',
  clientConfiguration: {},
  shareQrCodeAppLinkUrl: '',
  shareStatus: ''
}

ShareToApp.propTypes = {
  clientError: PropTypes.objectOf(PropTypes.any),
  clientConfiguration: PropTypes.shape({
    qr: PropTypes.shape({
      title: PropTypes.string,
    }),
    learnMoreLink: PropTypes.string,
    skinId: PropTypes.string,
    useResumeMobileFlow: PropTypes.bool.isRequired
  }),
  clientClosingType: PropTypes.string,
  shareError: PropTypes.objectOf(PropTypes.any),
  shareResuming: PropTypes.bool.isRequired,
  shareActive: PropTypes.bool.isRequired,
  shareStatus: PropTypes.string,
  shareActions: PropTypes.shape({
    initialise: PropTypes.func.isRequired,
    standByUntilUserReturn: PropTypes.func,
    openAppLink: PropTypes.func,
    // closeConnection: PropTypes.func.isRequired,
  }).isRequired,
  shareQrCodeAppLinkUrl: PropTypes.string,
}

export default withClientAndShare(ShareToApp)
